import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

import { commonPairs } from '../common.config';
import { CommonCrudService } from '../services/common-crud.service';
import * as CommonActions from './common.actions';

@Injectable()
export class CommonEffects {
    private _actions$ = inject(Actions);
    private logger = inject(LoggerService);
    private _crud = inject(CommonCrudService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all',
        connectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All
    getAll$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommonActions.loadAll),
            switchMap((action) => {
                // Get endpoint
                let _endpoint = commonPairs[action.entity].getAll;
                if (action.query) {
                    const endpoint = commonPairs[action.entity].getAll;
                    _endpoint = `${endpoint}/${action.query}`;
                }

                return this._crud.getAll(_endpoint).pipe(
                    map((res) => {
                        return CommonActions.loadAllSuccess({
                            data: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommonActions.loadAllFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One
    get$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommonActions.loadOne),
            switchMap((action) => {
                // Get endpoint
                const endpoint = commonPairs[action.entity];

                return this._crud.getOne(endpoint.getOne, action.id).pipe(
                    map((res) => {
                        return CommonActions.loadOneSuccess({
                            data: res.data,
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommonActions.loadOneFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Create
    create$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommonActions.create),
            switchMap((action) => {
                // Get endpoint
                const endpoint = commonPairs[action.entity];

                return this._crud.create(endpoint.create, action.data).pipe(
                    map((res) => {
                        return CommonActions.createSuccess({
                            data: res.data,
                            success: `${action.entity} created successfully`,
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommonActions.createFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Update
    update$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommonActions.update),
            switchMap((action) => {
                // Get endpoint
                const endpoint = commonPairs[action.entity];

                return this._crud
                    .update(endpoint.update, action.data, action.id)
                    .pipe(
                        map((res) => {
                            return CommonActions.updateSuccess({
                                data: res.data,
                                success: `${action.entity} updated successfully`,
                            });
                        }),
                        catchError((error) =>
                            of(
                                CommonActions.updateFailure({
                                    error:
                                        error?.error?.message ||
                                        this.messages.unknownError,
                                })
                            )
                        )
                    );
            })
        )
    );

    // Delete
    delete$ = createEffect(() =>
        this._actions$.pipe(
            ofType(CommonActions.deleteCommon),
            switchMap((action) => {
                // Get endpoint
                const endpoint = commonPairs[action.entity];

                return this._crud.delete(endpoint.delete, action.id).pipe(
                    map(() => {
                        return CommonActions.deleteSuccess({
                            id: action.id,
                            success: `${action.entity} deleted successfully`,
                        });
                    }),
                    catchError((error) =>
                        of(
                            CommonActions.deleteFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onCommonActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    CommonActions.createSuccess,
                    CommonActions.updateSuccess,
                    CommonActions.deleteSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onCommonActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    CommonActions.loadAllFailure,
                    CommonActions.loadOneFailure,
                    CommonActions.createFailure,
                    CommonActions.updateFailure,
                    CommonActions.deleteFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
